import React, { useState, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CHATBOT_CONSTANTS } from '../../../constants/chatbot';
import type { FeedbackType } from '../../../types/feedback';

export interface FeedbackFormProps {
  /** Message ID that feedback is being provided for */
  messageId: string;
  /** Type of feedback (SOLVED or NOT_HELPFUL) */
  feedbackType: FeedbackType;
  /** Callback when feedback is submitted */
  onSubmit: (text: string) => Promise<void>;
  /** Optional callback when form is cancelled */
  onCancel?: () => void;
}

/**
 * FeedbackForm Component
 *
 * Displays an inline feedback form with textarea for user input.
 * Appears below feedback buttons when "This didn't help" is clicked.
 * Includes character limit, privacy warning, and submit button.
 */
export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  messageId,
  feedbackType,
  onSubmit,
  onCancel,
}) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-focus textarea when form appears
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= CHATBOT_CONSTANTS.MAX_INPUT_LENGTH) {
      setFeedbackText(value);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!feedbackText.trim()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(feedbackText.trim());
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && feedbackText.trim()) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isSubmitDisabled = !feedbackText.trim() || isSubmitting;

  return (
    <div className='mt-4 w-full rounded-lg bg-gray-50 p-4'>
      <div className='flex flex-col gap-3'>
        {/* Form prompt */}
        <p className='text-sm text-neutral-950'>
          Thank you! Feel free to let me know if you have any details to share.
        </p>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={feedbackText}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder='Type your feedback here...'
          maxLength={CHATBOT_CONSTANTS.MAX_INPUT_LENGTH}
          disabled={isSubmitting}
          className={cn(
            'w-full rounded-md border border-gray-300 p-3',
            'min-h-[100px] resize-y',
            'text-sm text-gray-900 placeholder:text-gray-400',
            'focus:outline-none focus:border-gray-300',
            'disabled:bg-gray-100 disabled:cursor-not-allowed',
            'transition-colors duration-200',
          )}
          aria-label='Feedback text'
          aria-describedby='feedback-warning'
        />

        {/* Privacy warning */}
        <p id='feedback-warning' className='text-xs text-gray-500'>
          Please do not share any personal, confidential or sensitive
          information
        </p>

        {/* Error message */}
        {error && (
          <div className='text-sm text-red-600 bg-red-50 p-2 rounded-md'>
            {error}
          </div>
        )}

        {/* Submit button */}
        <div className='flex items-center gap-2'>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className={cn(
              'inline-flex items-center gap-2',
              'px-4 py-2',
              'rounded-lg',
              'border border-[#E5E5E5]',
              'bg-white hover:bg-gray-50',
              'text-gray-900 text-sm font-medium',
              'shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-colors duration-200',
            )}
            aria-label='Submit feedback'
          >
            {isSubmitting && <Loader2 className='h-4 w-4 animate-spin' />}
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>

          {onCancel && !isSubmitting && (
            <Button
              onClick={onCancel}
              variant='ghost'
              className='text-sm text-gray-600 hover:text-gray-900'
              aria-label='Cancel feedback'
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
